import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import styled from "styled-components";
import {
  useBLEforAquapilotWeb,
  UUID_WIFI_SERVICE,
  UUID_CHAR_WIFI_SSID,
  UUID_CHAR_WIFI_PASSWORD,
  UUID_CHAR_WIFI_ENC_METHOD,
} from "hooks/useBle";
import { trackEvent } from "utils/analytics";

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: flex-end;
`;

const StatusContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

interface ChangeWifiDialogProps {
  visible: boolean;
  onHide: () => void;
  deviceName: string;
}

const ChangeWifiDialog: React.FC<ChangeWifiDialogProps> = ({
  visible,
  onHide,
  deviceName,
}) => {
  // State for WiFi configuration
  const [ssid, setSsid] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<
    "idle" | "scanning" | "connecting" | "configuring" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Use the BLE hook
  const {
    scanForLifeguard,
    connectToDevice,
    disconnectFromDevice,
    sendWifiCredentials,
    isConnected,
    loading,
  } = useBLEforAquapilotWeb();

  // Reset state when dialog closes
  useEffect(() => {
    if (!visible) {
      if (isConnected) {
        disconnectFromDevice();
      }
      setStatus("idle");
      setErrorMessage("");
    }
  }, [visible, disconnectFromDevice, isConnected]);

  // Start the WiFi configuration process
  const startWifiConfiguration = async () => {
    if (!ssid || !password) {
      setErrorMessage("Please enter both SSID and password");
      return;
    }

    try {
      setStatus("scanning");
      setErrorMessage("");

      // Step 1: Scan for the device
      const device = await scanForLifeguard();
      if (!device) {
        setErrorMessage(
          "No device found. Make sure your device is in pairing mode.",
        );
        setStatus("error");
        return;
      }

      // Step 2: Connect to the device
      setStatus("connecting");
      const server = await connectToDevice(device);
      if (!server) {
        setErrorMessage("Failed to connect to the device.");
        setStatus("error");
        return;
      }

      // Step 3: Send WiFi credentials
      setStatus("configuring");
      await sendWifiCredentials(
        server,
        UUID_WIFI_SERVICE,
        UUID_CHAR_WIFI_SSID,
        UUID_CHAR_WIFI_PASSWORD,
        UUID_CHAR_WIFI_ENC_METHOD,
        ssid,
        password,
      );

      // Step 4: Success!
      setStatus("success");
      trackEvent("device_wifi_changed");

      // Automatically close and disconnect after success
      setTimeout(() => {
        disconnectFromDevice();
        onHide();
      }, 3000);
    } catch (error) {
      console.error("Error configuring WiFi:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "Unknown error occurred",
      );
      setStatus("error");
      disconnectFromDevice();
    }
  };

  const handleCancel = () => {
    if (isConnected) {
      disconnectFromDevice();
    }
    onHide();
  };

  // Render status message based on current status
  const renderStatus = () => {
    if (status === "idle" || !loading) return null;

    let statusMessage = "";
    switch (status) {
      case "scanning":
        statusMessage = "Scanning for device...";
        break;
      case "connecting":
        statusMessage = "Connecting to device...";
        break;
      case "configuring":
        statusMessage = "Configuring WiFi settings...";
        break;
      case "success":
        return (
          <Message severity="success" text="WiFi configuration successful!" />
        );
      case "error":
        return (
          <Message
            severity="error"
            text={errorMessage || "An error occurred"}
          />
        );
    }

    return (
      <StatusContainer>
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        <div>{statusMessage}</div>
      </StatusContainer>
    );
  };

  return (
    <Dialog
      header={`Change WiFi Settings for ${deviceName}`}
      visible={visible}
      onHide={handleCancel}
      style={{ width: "450px" }}
      modal
      dismissableMask={
        status !== "scanning" &&
        status !== "connecting" &&
        status !== "configuring"
      }
    >
      <DialogContent>
        <div className="p-field">
          <label htmlFor="wifi-ssid">WiFi Network (SSID)</label>
          <InputText
            id="wifi-ssid"
            name="wifi-ssid"
            autoComplete="off"
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            disabled={loading}
            className="p-inputtext-sm w-full"
          />
        </div>

        <div className="p-field">
          <label htmlFor="wifi-password">WiFi Password</label>
          <Password
            inputId="wifi-password"
            name="wifi-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            toggleMask
            feedback={false}
            disabled={loading}
            className="w-full"
            inputClassName="w-full"
          />
        </div>

        {renderStatus()}

        <ButtonContainer>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={handleCancel}
            disabled={loading && status !== "error"}
          />
          <Button
            label="Configure WiFi"
            icon="pi pi-wifi"
            onClick={startWifiConfiguration}
            disabled={loading || !ssid || !password}
            loading={loading && status !== "error"}
          />
        </ButtonContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeWifiDialog;
